import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { 
  List, 
  BarChart3, 
  Wallet, 
  Settings,
  LogOut,
  User,
  Lightbulb,
  Target,
  Calendar,
  RefreshCw,
  Users,
  Sparkles
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import NotificationBell from './NotificationBell';

const Layout = ({ children }) => {
  const { currentUser, signOut } = useAuth();
  const { theme, isDark } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  // Mobile bottom bar tabs
  const mobileNavItems = [
    { path: '/transactions', label: 'Trans.', icon: List },
    { path: '/stats', label: 'Stats', icon: BarChart3 },
    { path: '/accounts', label: 'Accounts', icon: Wallet },
    { path: '/settings', label: 'More', icon: Settings },
  ];

  // Full desktop sidebar tabs
  const desktopNavItems = [
    { path: '/transactions', fullLabel: 'Transactions', icon: List },
    { path: '/stats', fullLabel: 'Stats', icon: BarChart3 },
    { path: '/insights', fullLabel: 'AI Insights', icon: Lightbulb },
    { path: '/accounts', fullLabel: 'Accounts', icon: Wallet },
    { path: '/goals', fullLabel: 'Savings Goals', icon: Target },
    { path: '/bills', fullLabel: 'Bills & Reminders', icon: Calendar },
    { path: '/subscriptions', fullLabel: 'Subscriptions', icon: RefreshCw },
    { path: '/family', fullLabel: 'Family / Shared', icon: Users },
    { path: '/settings', fullLabel: 'Settings', icon: Settings },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen transition-colors duration-200" style={{ backgroundColor: theme.background }}>
      {/* Top Navigation Bar */}
      <header 
        className="fixed top-0 left-0 right-0 h-16 z-50 transition-colors duration-200" 
        style={{ backgroundColor: theme.cardBackground, boxShadow: isDark ? '0 2px 4px rgba(0, 0, 0, 0.3)' : '0 2px 4px rgba(0, 0, 0, 0.06)' }}
      >
        <div className="h-full px-4 md:px-6 flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <Link to="/transactions" className="flex items-center gap-2">
              <div className="flex items-center justify-center w-9 h-9 rounded-full shrink-0" style={{ backgroundColor: '#E8544F' }}>
                <Wallet className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-xl font-bold" style={{ color: theme.text }}>Spend Easy</h1>
            </Link>

            {currentUser?.isGuest && (
              <span 
                className="hidden sm:inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold text-white tracking-wide"
                style={{ backgroundColor: '#E8544F' }}
              >
                <Sparkles className="w-3 h-3" />
                Demo Mode
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-3">
            {currentUser && <NotificationBell />}

            {currentUser?.isGuest && (
              <button
                onClick={handleSignOut}
                className="text-xs px-3 py-1.5 rounded-md font-medium transition-colors"
                style={{ 
                  backgroundColor: isDark ? theme.inputBackground : '#F1F3F4',
                  color: theme.textSecondary 
                }}
              >
                Exit Demo
              </button>
            )}

            {currentUser && (
              <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
                <DropdownMenuTrigger asChild>
                  <button className="focus:outline-none">
                    <Avatar className="h-8 w-8 cursor-pointer ring-2 ring-transparent hover:ring-red-400 transition-all">
                      <AvatarImage src={currentUser.photoURL} alt={currentUser.displayName} />
                      <AvatarFallback style={{ backgroundColor: isDark ? theme.inputBackground : '#E9ECEF' }}>
                        <User className="h-4 w-4" style={{ color: theme.textSecondary }} />
                      </AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent 
                  align="end" 
                  className="w-48"
                  style={{ backgroundColor: theme.cardBackground, borderColor: theme.divider }}
                >
                  <div className="px-3 py-2 border-b" style={{ borderColor: theme.divider }}>
                    <p className="text-sm font-semibold truncate" style={{ color: theme.text }}>{currentUser.displayName || 'User'}</p>
                    <p className="text-xs truncate" style={{ color: theme.textSecondary }}>{currentUser.email}</p>
                  </div>
                  <DropdownMenuItem 
                    onClick={() => { navigate('/settings'); setDropdownOpen(false); }}
                    style={{ color: theme.text }}
                  >
                    <Settings className="mr-2 h-4 w-4" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleSignOut} style={{ color: '#E8544F' }}>
                    <LogOut className="mr-2 h-4 w-4" />
                    {currentUser.isGuest ? 'Exit Demo' : 'Sign Out'}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-16 pb-20 md:pb-8 md:pl-64">
        <div className="max-w-7xl mx-auto px-4 py-6">
          {children}
        </div>
      </main>

      {/* Bottom Navigation (Mobile) */}
      <nav 
        className="md:hidden fixed bottom-0 left-0 right-0 h-16 z-50 transition-colors duration-200" 
        style={{ backgroundColor: theme.tabBar, boxShadow: isDark ? '0 -2px 4px rgba(0, 0, 0, 0.3)' : '0 -2px 4px rgba(0, 0, 0, 0.06)' }}
      >
        <div className="h-full flex items-center justify-around">
          {mobileNavItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className="flex flex-col items-center justify-center flex-1 h-full transition-transform duration-200"
                style={{ backgroundColor: active ? (isDark ? theme.inputBackground : 'transparent') : 'transparent' }}
              >
                <Icon 
                  className="h-6 w-6" 
                  style={{ color: active ? theme.accent : theme.textSecondary }} 
                />
                <span 
                  className="text-xs mt-1" 
                  style={{ color: active ? theme.accent : theme.textSecondary, fontWeight: active ? 600 : 400 }}
                >
                  {item.label}
                </span>
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Sidebar (Desktop) */}
      <aside 
        className="hidden md:block fixed left-0 top-16 bottom-0 w-64 transition-colors duration-200 overflow-y-auto" 
        style={{ 
          backgroundColor: theme.cardBackground, 
          boxShadow: isDark ? '2px 0 4px rgba(0, 0, 0, 0.3)' : '2px 0 4px rgba(0, 0, 0, 0.06)',
          borderRight: `1px solid ${theme.divider}`
        }}
      >
        <nav className="py-4 space-y-1">
          {desktopNavItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className="flex items-center px-6 py-2.5 transition-all duration-150 relative text-sm font-medium"
                style={{
                  color: active ? theme.accent : theme.textSecondary,
                  backgroundColor: active ? (isDark ? theme.inputBackground : '#FEF2F2') : 'transparent',
                }}
              >
                {active && (
                  <div 
                    className="absolute left-0 top-0 bottom-0 w-1" 
                    style={{ backgroundColor: theme.accent }}
                  />
                )}
                <Icon className="h-4 w-4 mr-3 shrink-0" />
                <span className="truncate">{item.fullLabel}</span>
              </Link>
            );
          })}
        </nav>
      </aside>
    </div>
  );
};

export default Layout;
